import React, { useState, useEffect } from 'react';
import { HelpCircle, Search, MessageCircle, Phone, Mail, BookOpen, PlayCircle, ThumbsUp, ThumbsDown, Clock, User, Star, ChevronRight, Filter, Download, Share2 } from 'lucide-react';
import MockDataService from '../../../services/MockDataService';

const AgentHelpSupport = () => {
  const [helpData, setHelpData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('help-center');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    const fetchHelpData = async () => {
      try {
        setLoading(true);
        const response = await MockDataService.getAgentHelpSupport('agent-001');
        if (response.success) {
          setHelpData(response.data);
        } else {
          setError('Failed to load help data');
        }
      } catch (err) {
        setError('An error occurred while fetching help data.');
        console.error('Error fetching help data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHelpData();
  }, []);

  if (loading) {
    return <div className="p-6 text-center text-gray-500 dark:text-gray-400">Loading help and support data...</div>;
  }

  if (error) {
    return <div className="p-6 text-center text-red-500 dark:text-red-400">Error: {error}</div>;
  }

  if (!helpData) {
    return <div className="p-6 text-center text-gray-500 dark:text-gray-400">No help data available.</div>;
  }

  const { agentHelpSupport, helpCategories, supportStats } = helpData;
  const { helpCenter, supportTickets, liveChat, faq, videoTutorials, contactSupport, feedback } = agentHelpSupport;

  const tabs = [
    { id: 'help-center', label: 'Help Center', icon: BookOpen },
    { id: 'support-tickets', label: 'Support Tickets', icon: MessageCircle },
    { id: 'live-chat', label: 'Live Chat', icon: MessageCircle },
    { id: 'faq', label: 'FAQ', icon: HelpCircle },
    { id: 'video-tutorials', label: 'Video Tutorials', icon: PlayCircle },
    { id: 'contact-support', label: 'Contact Support', icon: Phone },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'help-center':
        return (
          <div className="space-y-6">
            {/* Search Bar */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search help articles..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                  Search
                </button>
              </div>
            </div>

            {/* Help Categories */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {helpCenter.categories.map((category) => (
                <div
                  key={category.id}
                  className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setSelectedCategory(category)}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                      <BookOpen className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">({category.articleCount})</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">{category.name}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{category.description}</p>
                  {/* <div className="flex items-center text-blue-600 dark:text-blue-400 text-sm font-medium">
                    Browse Articles
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </div> */}
                </div>
              ))}
            </div>

            {/* Popular Topics */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Popular Topics</h3>
              <div className="space-y-3">
                {helpCenter.popularTopics.map((topic) => (
                  <div key={topic.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-gray-100">{topic.title}</p>
                      <div className="flex items-center space-x-4 mt-1">
                        <span className="text-sm text-gray-500 dark:text-gray-400">{topic.views} views</span>
                        <div className="flex items-center space-x-1">
                          <ThumbsUp className="h-3 w-3 text-green-500" />
                          <span className="text-sm text-gray-500 dark:text-gray-400">{topic.helpful}</span>
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </div>
                ))}
              </div>
            </div>

            {/* Selected Category Articles */}
            {selectedCategory && (
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{selectedCategory.name} Articles</h3>
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    ×
                  </button>
                </div>
                <div className="space-y-3">
                  {selectedCategory.articles.map((article) => (
                    <div key={article.id} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer">
                      <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{article.title}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{article.content.substring(0, 150)}...</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <span className="text-sm text-gray-500 dark:text-gray-400">{article.views} views</span>
                          <div className="flex items-center space-x-1">
                            <ThumbsUp className="h-3 w-3 text-green-500" />
                            <span className="text-sm text-gray-500 dark:text-gray-400">{article.helpful}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <ThumbsDown className="h-3 w-3 text-red-500" />
                            <span className="text-sm text-gray-500 dark:text-gray-400">{article.notHelpful}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                            <Share2 className="h-4 w-4" />
                          </button>
                          <button className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                            <Download className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'support-tickets':
        return (
          <div className="space-y-6">
            {/* Support Tickets Header */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">My Support Tickets</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Manage your support requests</p>
              </div>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                Create New Ticket
              </button>
            </div>

            {/* Support Tickets List */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="p-6">
                <div className="space-y-4">
                  {supportTickets.map((ticket) => (
                    <div key={ticket.id} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-md">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h4 className="font-medium text-gray-900 dark:text-gray-100">{ticket.subject}</h4>
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              ticket.priority === 'high' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                              ticket.priority === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                              'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            }`}>
                              {ticket.priority}
                            </span>
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              ticket.status === 'open' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                              ticket.status === 'in-progress' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                              'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            }`}>
                              {ticket.status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{ticket.description}</p>
                          <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                            <span>Category: {ticket.category}</span>
                            <span>Created: {new Date(ticket.createdDate).toLocaleDateString()}</span>
                            <span>Updated: {new Date(ticket.lastUpdated).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button className="px-3 py-1 text-sm bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-md hover:bg-blue-200 dark:hover:bg-blue-800">
                            View Details
                          </button>
                        </div>
                      </div>
                      {ticket.responses.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                          <h5 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Responses:</h5>
                          {ticket.responses.map((response) => (
                            <div key={response.id} className="p-3 bg-white dark:bg-gray-800 rounded-md mb-2">
                              <p className="text-sm text-gray-700 dark:text-gray-300">{response.content}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {new Date(response.timestamp).toLocaleString()}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'live-chat':
        return (
          <div className="space-y-6">
            {/* Live Chat Status */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${liveChat.available ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Live Chat Support</h3>
                </div>
                <span className={`px-3 py-1 text-sm font-semibold rounded-full ${
                  liveChat.available ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                }`}>
                  {liveChat.available ? 'Available' : 'Unavailable'}
                </span>
              </div>
              
              {liveChat.available && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-md">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Queue Position</p>
                      <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{liveChat.queuePosition}</p>
                    </div>
                    <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-md">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Estimated Wait</p>
                      <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{liveChat.estimatedWaitTime} min</p>
                    </div>
                    <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-md">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Available Agents</p>
                      <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {liveChat.agents.filter(agent => agent.status === 'available').length}
                      </p>
                    </div>
                  </div>
                  
                  <button className="w-full px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                    Start Live Chat
                  </button>
                </div>
              )}
            </div>

            {/* Available Agents */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Available Agents</h3>
              <div className="space-y-3">
                {liveChat.agents.map((agent) => (
                  <div key={agent.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                        <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">{agent.name}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{agent.specialty}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-1">
                        <Star className="h-4 w-4 text-yellow-500" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">{agent.rating}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">{agent.responseTime} min</span>
                      </div>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        agent.status === 'available' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                      }`}>
                        {agent.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'faq':
        return (
          <div className="space-y-6">
            {/* FAQ Header */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Frequently Asked Questions</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Find quick answers to common questions</p>
              </div>
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-gray-400" />
                <select className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100">
                  <option value="">All Categories</option>
                  <option value="Account Management">Account Management</option>
                  <option value="Ticket Management">Ticket Management</option>
                  <option value="System Features">System Features</option>
                  <option value="Training & Guides">Training & Guides</option>
                </select>
              </div>
            </div>

            {/* FAQ Items */}
            <div className="space-y-4">
              {faq.map((item) => (
                <div key={item.id} className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100">{item.question}</h4>
                    <span className="px-2 py-1 text-xs font-semibold bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full">
                      {item.category}
                    </span>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 mb-4">{item.answer}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-1">
                        <ThumbsUp className="h-4 w-4 text-green-500" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">{item.helpful}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <ThumbsDown className="h-4 w-4 text-red-500" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">{item.notHelpful}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button className="px-3 py-1 text-sm bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-md hover:bg-green-200 dark:hover:bg-green-800">
                        Helpful
                      </button>
                      <button className="px-3 py-1 text-sm bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded-md hover:bg-red-200 dark:hover:bg-red-800">
                        Not Helpful
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'video-tutorials':
        return (
          <div className="space-y-6">
            {/* Video Tutorials Header */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Video Tutorials</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Learn with step-by-step video guides</p>
              </div>
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-gray-400" />
                <select className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100">
                  <option value="">All Categories</option>
                  <option value="Getting Started">Getting Started</option>
                  <option value="Ticket Management">Ticket Management</option>
                  <option value="System Features">System Features</option>
                  <option value="Training & Guides">Training & Guides</option>
                </select>
              </div>
            </div>

            {/* Video Tutorials Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {videoTutorials.map((video) => (
                <div key={video.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="aspect-video bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                    <PlayCircle className="h-16 w-16 text-gray-400" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        video.difficulty === 'beginner' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        video.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                        'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {video.difficulty}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">{Math.floor(video.duration / 60)} min</span>
                    </div>
                    <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">{video.title}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{video.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-1">
                          <PlayCircle className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600 dark:text-gray-400">{video.views}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Star className="h-4 w-4 text-yellow-500" />
                          <span className="text-sm text-gray-600 dark:text-gray-400">{video.rating}</span>
                        </div>
                      </div>
                      <button className="px-3 py-1 text-sm bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-md hover:bg-blue-200 dark:hover:bg-blue-800">
                        Watch
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'contact-support':
        return (
          <div className="space-y-6">
            {/* Contact Support Options */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Email Support */}
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                    <Mail className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Email Support</h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Send us an email and we'll get back to you within {contactSupport.email.responseTime}.
                </p>
                <div className="space-y-2 mb-4">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <strong>Email:</strong> {contactSupport.email.address}
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <strong>Response Time:</strong> {contactSupport.email.responseTime}
                  </p>
                </div>
                <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                  Send Email
                </button>
              </div>

              {/* Phone Support */}
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                    <Phone className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Phone Support</h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Call us during business hours for immediate assistance.
                </p>
                <div className="space-y-2 mb-4">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <strong>Phone:</strong> {contactSupport.phone.number}
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <strong>Hours:</strong> {contactSupport.phone.hours}
                  </p>
                </div>
                <button className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors">
                  Call Now
                </button>
              </div>

              {/* Live Chat */}
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                    <MessageCircle className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Live Chat</h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Chat with our support team in real-time.
                </p>
                <div className="space-y-2 mb-4">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <strong>Hours:</strong> {contactSupport.liveChat.hours}
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <strong>Avg Wait:</strong> {contactSupport.liveChat.averageWaitTime}
                  </p>
                </div>
                <button className="w-full px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors">
                  Start Chat
                </button>
              </div>
            </div>

            {/* Support Statistics */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Support Statistics</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{supportStats.totalArticles}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Help Articles</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{supportStats.totalFAQ}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">FAQ Items</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{supportStats.totalVideos}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Video Tutorials</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{supportStats.satisfactionScore}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Satisfaction Score</p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <HelpCircle className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Help & Support
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Get help and support for your work
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors">
                <Search className="h-4 w-4 inline-block mr-2" />
                Search Help
              </button>
              <button className="px-4 py-2 text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors">
                <MessageCircle className="h-4 w-4 inline-block mr-2" />
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Support Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Help Articles</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{supportStats.totalArticles}</p>
              </div>
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <BookOpen className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">FAQ Items</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{supportStats.totalFAQ}</p>
              </div>
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <HelpCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Video Tutorials</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{supportStats.totalVideos}</p>
              </div>
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <PlayCircle className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Satisfaction</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{supportStats.satisfactionScore}</p>
              </div>
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                <Star className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 mb-8">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-8 px-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                    ${activeTab === tab.id
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                    }
                  `}
                >
                  <tab.icon className="h-4 w-4 inline-block mr-2" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
          <div className="p-6">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentHelpSupport;
